import { useCallback, useRef, useState } from "react";
import {
  useRequestDocumentUploadMutation,
  useConfirmDocumentUploadMutation,
  useAttachDocumentToTaskMutation,
  useDetachDocumentFromTaskMutation,
  useDeleteDocumentMutation,
  useWorkflowDocumentsQuery,
  WorkflowDocumentsDocument,
} from "#graphql/supergraphHooks";
import { onApiError } from "../utils/apiErrorToast";
import type { WorkflowDocumentsQuery, TaskDocumentPurpose } from "#graphql/supergraphTypes";

type TaskDocumentAttachment = WorkflowDocumentsQuery["workflowDocuments"][number];

interface PendingDocument {
  documentId: number;
  taskId: number;
  purpose: TaskDocumentPurpose;
  isExternal: boolean;
}

export interface StagedDocument {
  documentId: number;
  fileName: string;
  fileSizeBytes: number;
  taskId: number;
}

export interface StageDocumentOptions {
  taskId: number;
  purpose: TaskDocumentPurpose;
  isExternal?: boolean;
}

interface UseDocumentsResult {
  documents: TaskDocumentAttachment[];
  stagedDocuments: StagedDocument[];
  loading: boolean;
  isUploadInProgress: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
  stageDocument: (file: File, options: StageDocumentOptions) => Promise<number>;
  deleteUploadedDocument: (documentId: number) => Promise<void>;
  flushPendingAttachments: () => Promise<void>;
  detachObsoleteDocuments: (taskId: number, desiredDocumentIds: number[]) => Promise<void>;
}

export function useDocuments(workflowId: string): UseDocumentsResult {
  const parsedId = workflowId ? Number(workflowId) : NaN;
  const skip = !Number.isFinite(parsedId);

  const {
    data,
    loading,
    error: queryError,
    refetch,
  } = useWorkflowDocumentsQuery({
    variables: { workflowId: parsedId },
    skip,
    fetchPolicy: "network-only",
  });

  const refetchDocs = skip
    ? {}
    : {
        refetchQueries: [
          { query: WorkflowDocumentsDocument, variables: { workflowId: parsedId } },
        ],
      };

  const [requestUpload] = useRequestDocumentUploadMutation();
  const [confirmUpload] = useConfirmDocumentUploadMutation();
  const [attachToTask] = useAttachDocumentToTaskMutation({
    ...refetchDocs,
    onError: onApiError,
  });
  const [detachFromTask] = useDetachDocumentFromTaskMutation({
    ...refetchDocs,
    onError: onApiError,
  });
  const [deleteDocument] = useDeleteDocumentMutation();

  const pendingRef = useRef<PendingDocument[]>([]);
  const [stagedDocuments, setStagedDocuments] = useState<StagedDocument[]>([]);
  const uploadCountRef = useRef(0);
  const [isUploadInProgress, setIsUploadInProgress] = useState(false);

  const stageDocument = useCallback(
    async (file: File, options: StageDocumentOptions): Promise<number> => {
      const { taskId, purpose, isExternal = false } = options;

      uploadCountRef.current += 1;
      setIsUploadInProgress(true);
      try {
        const { data: reqData } = await requestUpload({
          variables: {
            input: {
              taskId,
              fileName: file.name,
              contentType: file.type,
              fileSizeBytes: file.size,
            },
          },
        });
        if (!reqData?.requestDocumentUpload) throw new Error("Failed to get upload URL");

        const { documentId, uploadUrl, contentType } = reqData.requestDocumentUpload;

        const s3Res = await fetch(uploadUrl, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": contentType },
        });
        if (!s3Res.ok) throw new Error(`S3 upload failed: ${s3Res.status}`);

        await confirmUpload({ variables: { documentId } });

        pendingRef.current = [
          ...pendingRef.current,
          { documentId, taskId, purpose, isExternal },
        ];
        setStagedDocuments((prev) => [
          ...prev,
          { documentId, fileName: file.name, fileSizeBytes: file.size, taskId },
        ]);

        return documentId;
      } finally {
        uploadCountRef.current -= 1;
        if (uploadCountRef.current === 0) {
          setIsUploadInProgress(false);
        }
      }
    },
    [requestUpload, confirmUpload],
  );

  const deleteUploadedDocument = useCallback(
    async (documentId: number): Promise<void> => {
      await deleteDocument({ variables: { documentId } });
      pendingRef.current = pendingRef.current.filter((p) => p.documentId !== documentId);
      setStagedDocuments((prev) => prev.filter((d) => d.documentId !== documentId));
    },
    [deleteDocument],
  );

  const detachObsoleteDocuments = useCallback(
    async (taskId: number, desiredDocumentIds: number[]): Promise<void> => {
      const desiredSet = new Set(desiredDocumentIds);
      const stale = (data?.workflowDocuments ?? []).filter(
        (d) => d.taskId === taskId && !desiredSet.has(d.documentId),
      );
      if (!stale.length) return;
      await Promise.all(
        stale.map((d) => detachFromTask({ variables: { taskId, documentId: d.documentId } })),
      );
    },
    [data?.workflowDocuments, detachFromTask],
  );

  const flushPendingAttachments = useCallback(async (): Promise<void> => {
    const pending = pendingRef.current;
    if (!pending.length) return;
    await Promise.all(
      pending.map(({ documentId, taskId, purpose, isExternal }) =>
        attachToTask({
          variables: {
            input: {
              taskId,
              documentId,
              purpose,
              isExternal,
              viewerGrants: [],
            },
          },
        }),
      ),
    );
    pendingRef.current = [];
    setStagedDocuments([]);
  }, [attachToTask]);

  return {
    documents: data?.workflowDocuments ?? [],
    stagedDocuments,
    loading,
    isUploadInProgress,
    error: queryError ?? null,
    refetch,
    stageDocument,
    deleteUploadedDocument,
    flushPendingAttachments,
    detachObsoleteDocuments,
  };
}
